/**
 * Sorting by tapping a column, for the tables that have a header row.
 *
 * ## Why the header and not a control
 *
 * The console had one `Sort` pill offering a fixed menu — `stalled`,
 * `attention`, `severity` — a vocabulary of the server's own that names
 * orderings rather than columns. On a table that is the wrong shape twice
 * over: the reader is looking at a column and wants THAT column ordered, and
 * the pill could not offer most of them (there was no "by assignee", no "by
 * due date"). Tapping the header is the ordering the reader is already
 * pointing at (user-directed, 2026-09-11).
 *
 * ## Client-side, and complete because of it
 *
 * These views read the whole workspace in one request — that is what makes
 * their counts trustworthy and their filters instant — so the sort runs over
 * every row and the page is drawn from the sorted whole. A server sort would
 * be a round trip to reorder rows the browser already has, and a sort over
 * one page would be a lie about the order.
 *
 * The rows are sorted BEFORE they are paged, which is the half that is easy
 * to get wrong: sorting the visible slice would reorder forty rows and leave
 * the rest where they were.
 *
 * ## It lives in the URL
 *
 * A sorted table is a link somebody can send, which is the same reason every
 * filter on these pages is a parameter rather than component state.
 */

export type SortDirection = "asc" | "desc";

export interface ColumnSort {
  column: string;
  direction: SortDirection;
}

const COLUMN_PARAM = "col";
const DIRECTION_PARAM = "dir";

/** The sort in force, or `null` for the order the server sent. */
export function readColumnSort(search: string): ColumnSort | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const column = params.get(COLUMN_PARAM);
  if (!column) return null;
  return { column, direction: params.get(DIRECTION_PARAM) === "desc" ? "desc" : "asc" };
}

/** The two parameters a sort writes, as `[key, value]` pairs — `null` clears
 *  them. Returned rather than applied so the caller keeps ownership of its
 *  own URL. */
export function columnSortParams(sort: ColumnSort | null): Array<[string, string | null]> {
  return [
    [COLUMN_PARAM, sort ? sort.column : null],
    [DIRECTION_PARAM, sort ? sort.direction : null],
  ];
}

/**
 * Order `rows` by the active column.
 *
 * `compare` is the table's own map of column to comparator, because what
 * "sorted by priority" means is a property of the table, not of this module.
 * A column with no comparator leaves the order alone rather than throwing: a
 * stale `?col=` in somebody's bookmark should show them the table, not an
 * error.
 *
 * STABLE, via the index tiebreak. `Array.prototype.sort` is specified stable
 * in modern engines, but the rows arriving here have already been through
 * one sort on the server, and an explicit tiebreak is cheaper to reason
 * about than a guarantee two layers away.
 */
export function sortByColumn<T>(
  rows: T[],
  sort: ColumnSort | null,
  compare: Record<string, (a: T, b: T) => number>,
): T[] {
  if (!sort) return rows;
  const comparator = compare[sort.column];
  if (!comparator) return rows;
  const sign = sort.direction === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const verdict = comparator(a.row, b.row);
      return verdict !== 0 ? verdict * sign : a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * Compare two values that may be missing.
 *
 * Blanks sort LAST in both directions, and that is deliberate: "no assignee"
 * and "no due date" are absences, and an absence is not the smallest value —
 * a reader sorting by due date wants the dated rows in order, not a screen
 * of undated ones first. Reversing the direction should not bring them to
 * the top.
 */
export function compareMaybe<V>(
  a: V | undefined | null,
  b: V | undefined | null,
  direction: SortDirection,
  compare: (left: V, right: V) => number,
): number {
  const aMissing = a === undefined || a === null || a === "";
  const bMissing = b === undefined || b === null || b === "";
  if (aMissing && bMissing) return 0;
  // The sign is undone by the caller's direction, so flip it here to keep
  // blanks at the bottom either way.
  if (aMissing) return direction === "asc" ? 1 : -1;
  if (bMissing) return direction === "asc" ? -1 : 1;
  return compare(a as V, b as V);
}

/** `text` comparison the way a reader expects a name column to sort —
 *  case-insensitive, and `10` after `9` rather than before it. */
export function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
}
